// Resolves the brand name for assistant-facing copy ("Ask <name>",
// "Ask your <name> assistant"). Centralised here so every call site reads
// the same rule instead of six ad-hoc conditionals (plan
// docs/plans-pending/2026-09-17-the-assistant-wears-the-partner-brand.md §D).
import { isVerityHost, useBranding } from "@/context/BrandingContext";

/**
 * Returns the assistant's brand name, or `null` when no trustworthy name is
 * available yet — callers must then fall back to brand-free wording ("Ask
 * the assistant") rather than interpolating anything.
 *
 * While branding is not yet `"ready"` (loading, or a failed resolve that fell
 * back), `branding.productName` on a partner/unknown host is the neutral
 * placeholder `"Loading…"` (see NEUTRAL_BRANDING in BrandingContext.tsx) —
 * showing that would read worse than the brand-free wording it replaces. On
 * the platform's own hosts (`isVerityHost`) the not-ready value is already the
 * correct platform default, so the branded wording is kept there.
 */
export function useAssistantName(): string | null {
  const { branding, state } = useBranding();
  if (state !== "ready" && !isVerityHost()) return null;
  return branding.shortName ?? branding.productName;
}
